import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useLocation } from 'react-router-dom'
import { getCursorConfig } from '../api/cursor'
import { getAdminAllProjectsKey, listMcpKeys, rotateOwnMcpKey } from '../api/mcp-keys'
import { listOAuthGrants, revokeOAuthGrant } from '../api/mcp-oauth'
import {
  getHarnessMcpConfig,
  type HarnessMcpConfigResponse,
  type McpAuthMethod,
} from '../api/skill'
import { useDialog } from '../components/dialogs/useDialog'
import McpQuickSetup from '../components/mcp/McpQuickSetup'
import OAuthGrantsSection from '../components/mcp/OAuthGrantsSection'
import { MCP_HARNESS_OPTIONS } from '../components/setup/harnessMeta'
import SetupGuideShell from '../components/setup/SetupGuideShell'
import SetupStepper from '../components/setup/SetupStepper'
import TokenRevealModal from '../components/TokenRevealModal'
import { useAuth } from '../hooks/useAuth'
import type { CursorConfigResponse, McpKeyListItem, McpOAuthGrant } from '../types/api'
import { isGlobalAdmin } from '../utils/rbac'

function ConfigBlock({
  title,
  description,
  text,
  copyLabel,
  copied,
  onCopy,
  copyDisabled,
  copyHint,
}: {
  title: string
  description?: string
  text: string
  copyLabel: string
  copied: string | null
  onCopy: (text: string, label: string) => void
  copyDisabled?: boolean
  copyHint?: string
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-harbor-text">{title}</h3>
      {description && <p className="text-sm text-harbor-text-secondary">{description}</p>}
      <div className="rounded-lg bg-harbor-surface-raised p-4">
        <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
          {text}
        </pre>
      </div>
      <button
        type="button"
        onClick={() => onCopy(text, copyLabel)}
        disabled={copyDisabled}
        className="rounded-lg bg-harbor-border px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-harbor-surface-raised disabled:cursor-not-allowed disabled:opacity-50"
      >
        {copied === copyLabel ? 'Copied!' : 'Copy Configuration'}
      </button>
      {copyDisabled && copyHint && <p className="text-xs text-amber-200/90">{copyHint}</p>}
    </div>
  )
}

export default function McpSetupPage() {
  const dialog = useDialog()
  const { user } = useAuth()
  const location = useLocation()
  const [cursorConfig, setCursorConfig] = useState<CursorConfigResponse | null>(null)
  const [ownMcpKey, setOwnMcpKey] = useState<McpKeyListItem | null>(null)
  const [isLoadingKey, setIsLoadingKey] = useState(true)
  const [isRotatingKey, setIsRotatingKey] = useState(false)
  const [revealedToken, setRevealedToken] = useState<string | null>(null)
  const [embeddedKey, setEmbeddedKey] = useState<string | null>(null)
  const [harness, setHarness] = useState('cursor')
  const [harnessConfig, setHarnessConfig] = useState<HarnessMcpConfigResponse | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState(0)
  // Credential choice for remote MCP: paste a static `ch_` key, or add the
  // server by URL and sign in via the browser (Hydra OAuth, issue #112).
  const [authMethod, setAuthMethod] = useState<McpAuthMethod>(() =>
    localStorage.getItem('mcp-auth-method') === 'oauth' ? 'oauth' : 'api_key'
  )
  // Null = server has MCP OAuth disabled (or the fetch failed) → preview note.
  const [oauthGrants, setOauthGrants] = useState<McpOAuthGrant[] | null>(null)
  const [isLoadingGrants, setIsLoadingGrants] = useState(true)
  const [isRevokingGrantId, setIsRevokingGrantId] = useState<number | null>(null)
  const isAdmin = isGlobalAdmin(user?.role)

  const fetchConfig = useCallback(async () => {
    try {
      const data = await getCursorConfig()
      setCursorConfig(data)
    } catch {
      // Config fetch failed — not critical
    }
  }, [])

  useEffect(() => {
    void fetchConfig()
  }, [fetchConfig])

  const loadOwnKey = useCallback(async () => {
    try {
      const data = await listMcpKeys()
      const key = data.keys.find((k) => k.scope === 'acl' && k.isActive) ?? null
      setOwnMcpKey(key)
    } catch {
      toast.error('Failed to load your MCP API key')
    } finally {
      setIsLoadingKey(false)
    }
  }, [])

  useEffect(() => {
    void loadOwnKey()
  }, [loadOwnKey])

  const loadOAuthGrants = useCallback(async () => {
    try {
      const data = await listOAuthGrants()
      setOauthGrants(data.grants)
    } catch {
      // 501 when the server has MCP OAuth disabled, or any other failure —
      // show the preview note instead of the list.
      setOauthGrants(null)
    } finally {
      setIsLoadingGrants(false)
    }
  }, [])

  useEffect(() => {
    void loadOAuthGrants()
  }, [loadOAuthGrants])

  const handleRevokeGrant = async (id: number) => {
    const confirmed = await dialog.confirm({
      title: 'Revoke OAuth grant?',
      description: 'The client will lose MCP access immediately.',
      confirmLabel: 'Revoke grant',
      tone: 'danger',
    })
    if (!confirmed) return

    setIsRevokingGrantId(id)
    try {
      await revokeOAuthGrant(id)
      await loadOAuthGrants()
      toast.success('OAuth grant revoked')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to revoke OAuth grant')
    } finally {
      setIsRevokingGrantId(null)
    }
  }

  // Admins embed the retrievable all-projects token on page load. Non-admins
  // only ever see the full key once, right after Generate/Rotate in step 1.
  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    getAdminAllProjectsKey()
      .then((data) => {
        if (!cancelled && data.mcpKeyToken) setEmbeddedKey(data.mcpKeyToken)
      })
      .catch(() => {
        // Admin key fetch failed — the user can still rotate in step 1.
      })
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  useEffect(() => {
    let cancelled = false
    getHarnessMcpConfig(harness, authMethod)
      .then((data) => {
        if (!cancelled) setHarnessConfig(data)
      })
      .catch(() => {
        if (!cancelled) setHarnessConfig(null)
      })
    return () => {
      cancelled = true
    }
  }, [harness, authMethod])

  const handleAuthMethodChange = (method: McpAuthMethod) => {
    setAuthMethod(method)
    localStorage.setItem('mcp-auth-method', method)
  }

  const handleRotateKey = async () => {
    const confirmed = await dialog.confirm({
      title: 'Rotate MCP API key?',
      description: 'The previous key will stop working immediately.',
      confirmLabel: 'Rotate key',
      tone: 'danger',
    })
    if (!confirmed) return

    setIsRotatingKey(true)
    try {
      const rotated = await rotateOwnMcpKey()
      setRevealedToken(rotated.token)
      setEmbeddedKey(rotated.token)
      await loadOwnKey()
      toast.success('MCP API key rotated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rotate MCP API key')
    } finally {
      setIsRotatingKey(false)
    }
  }

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const placeholder = harnessConfig?.apiKeyPlaceholder ?? 'YOUR_MCP_API_KEY'
  const remoteMcpUrl = harnessConfig?.remoteMcpUrl ?? cursorConfig?.remoteMcpUrl ?? null
  const isOAuth = authMethod === 'oauth'
  // OAuth availability: explicit server flag wins; fall back to the grants
  // probe (non-null = the server answered the grants list, so OAuth is on).
  const oauthEnabled =
    harnessConfig?.oauthEnabled ?? cursorConfig?.oauthEnabled ?? oauthGrants !== null
  const configText = harnessConfig
    ? isOAuth
      ? harnessConfig.configText
      : embeddedKey
        ? harnessConfig.configText.split(placeholder).join(embeddedKey)
        : harnessConfig.configText
    : null
  // OAuth configs carry no secret — copy works as soon as the server URL is
  // known. API-key configs need the real key embedded first.
  const copyBlocked = isOAuth ? !remoteMcpUrl : !embeddedKey
  const harnessLabel = MCP_HARNESS_OPTIONS.find((o) => o.value === harness)?.label ?? harness
  const verifyPrompt =
    `My Context Harbor MCP client is connected. Call the setup_agent_harness MCP tool ` +
    `with project_name="<project>" and harness="${harness}", and confirm you get back ` +
    `the install payload (skill file + AGENTS.md block) — that proves remote MCP is wired up. ` +
    `Replace <project> with my project name before running.`

  return (
    <SetupGuideShell
      pathname={location.pathname}
      title="MCP Setup"
      description="Connect your agent (Claude Code, Cursor, OpenCode, …) to this deployment via Streamable HTTP at /mcp."
    >
      {!cursorConfig?.ragServerBaseUrl && (
        <div className="space-y-2 rounded-xl border border-amber-700/60 bg-amber-900/20 p-5">
          <h2 className="text-lg font-semibold text-amber-200">Set the public base URL</h2>
          <p className="text-sm text-amber-100/80">
            Ask an admin to set <code className="text-amber-200">FRONTEND_URL</code> in the
            server&apos;s <code className="text-amber-200">.env</code> file (e.g.{' '}
            <code className="text-amber-200">https://contextharbor.example.com</code>) and restart
            the server so the remote MCP config can be generated.
          </p>
        </div>
      )}

      <div className="space-y-2 rounded-xl border border-harbor-border bg-harbor-surface p-5">
        <h2 className="text-base font-semibold text-harbor-text">
          How should your client connect?
        </h2>
        <div
          className="flex flex-col gap-2 sm:flex-row"
          role="radiogroup"
          aria-label="MCP auth method"
        >
          <label
            className={`flex flex-1 cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              !isOAuth ? 'border-emerald-600 bg-emerald-900/20' : 'border-harbor-border'
            }`}
          >
            <input
              type="radio"
              name="mcp-auth-method"
              value="api_key"
              checked={!isOAuth}
              onChange={() => handleAuthMethodChange('api_key')}
              className="mt-1 accent-emerald-500"
            />
            <span>
              <span className="block text-sm font-medium text-harbor-text">API key</span>
              <span className="block text-xs text-harbor-text-secondary">
                Use quick setup or configure your client manually. No server OAuth setup required.
              </span>
            </span>
          </label>
          <label
            className={`flex flex-1 cursor-pointer items-start gap-3 rounded-lg border p-3 ${
              isOAuth ? 'border-emerald-600 bg-emerald-900/20' : 'border-harbor-border'
            }`}
          >
            <input
              type="radio"
              name="mcp-auth-method"
              value="oauth"
              checked={isOAuth}
              onChange={() => handleAuthMethodChange('oauth')}
              className="mt-1 accent-emerald-500"
            />
            <span>
              <span className="block text-sm font-medium text-harbor-text">
                Sign in with OAuth {!oauthEnabled && '(unavailable)'}
              </span>
              <span className="block text-xs text-harbor-text-secondary">
                Add the server by URL — sign in in the browser, no key to paste or rotate.
              </span>
            </span>
          </label>
        </div>
        {isOAuth && !oauthEnabled && (
          <p className="text-xs text-amber-200/90">
            URL-only connectors are unavailable until an admin enables MCP OAuth on the server
            (MCP_OAUTH_ENABLED + Hydra). Bearer API keys keep working.
          </p>
        )}
      </div>

      {!isOAuth && <McpQuickSetup configured={!!remoteMcpUrl} />}

      <SetupStepper
        activeStep={activeStep}
        onStepChange={setActiveStep}
        steps={[
          {
            title: isOAuth ? 'Add the server by URL' : 'Get your API key',
            children: isOAuth ? (
              <div className="space-y-4">
                <p>
                  No key to copy. Add this server URL in your MCP client — it discovers{' '}
                  <code className="text-emerald-400">/.well-known/oauth-protected-resource</code>{' '}
                  and opens the browser so you can sign in and approve access (scopes are
                  audience-bound to this deployment&apos;s /mcp resource).
                </p>
                {remoteMcpUrl ? (
                  <div className="rounded-lg bg-harbor-surface-raised p-4">
                    <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                      {remoteMcpUrl}
                    </pre>
                  </div>
                ) : (
                  <p className="text-harbor-text-muted">
                    The server URL is unavailable until an admin sets the public base URL in{' '}
                    <Link
                      to="/admin/settings"
                      className="text-emerald-400 underline hover:text-emerald-300"
                    >
                      System Settings
                    </Link>
                    .
                  </p>
                )}
                <ol className="list-decimal space-y-1 pl-5 text-sm text-harbor-text-secondary">
                  <li>Add the URL above in your client (see the per-harness config in step 2).</li>
                  <li>Sign in with your ContextHarbor account when the browser opens.</li>
                  <li>Review the client name, scopes, and audience, then choose Allow.</li>
                </ol>
                <button
                  type="button"
                  onClick={() => remoteMcpUrl && void copyToClipboard(remoteMcpUrl, 'oauth-url')}
                  disabled={!remoteMcpUrl}
                  className="rounded-lg bg-harbor-border px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-harbor-surface-raised disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {copied === 'oauth-url' ? 'Copied!' : 'Copy Server URL'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p>
                  {isAdmin
                    ? 'Bearer token for remote MCP at /mcp — the all-projects admin key is embedded into the configs below automatically.'
                    : 'Bearer token for remote MCP at /mcp. Only the prefix is stored — rotate to generate a new key (full secret shown once, then embedded into the configs below).'}{' '}
                  New keys start with <code className="text-emerald-400">ch_</code> — older
                  64-character hex keys remain valid until rotated.
                </p>
                {isLoadingKey ? (
                  <div className="flex justify-center py-4">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-emerald-500" />
                  </div>
                ) : ownMcpKey ? (
                  <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-harbor-text-muted">Prefix</dt>
                      <dd className="font-mono text-harbor-text">
                        {ownMcpKey.tokenPrefix ?? '—'}…
                      </dd>
                    </div>
                    <div>
                      <dt className="text-harbor-text-muted">Last used</dt>
                      <dd className="text-harbor-text">
                        {ownMcpKey.lastUsedAt
                          ? new Date(ownMcpKey.lastUsedAt).toLocaleString()
                          : 'Never'}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-harbor-text-muted">No active MCP API key yet.</p>
                )}
                <button
                  type="button"
                  onClick={() => void handleRotateKey()}
                  disabled={isRotatingKey}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isRotatingKey ? 'Rotating...' : ownMcpKey ? 'Rotate key' : 'Generate key'}
                </button>
                {embeddedKey && (
                  <p className="text-xs text-emerald-300">
                    Key ready — the configs in step 2 already include it, so copy-paste works as-is.
                  </p>
                )}
              </div>
            ),
          },
          {
            title: 'Connect your client',
            children: (
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="mcp-harness"
                    className="mb-1 block text-sm font-medium text-harbor-text-secondary"
                  >
                    Agent / harness
                  </label>
                  <select
                    id="mcp-harness"
                    value={harness}
                    onChange={(e) => setHarness(e.target.value)}
                    className="w-full max-w-md rounded-lg border border-harbor-border bg-harbor-surface-raised px-3 py-2 text-harbor-text focus:border-transparent focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {MCP_HARNESS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {harnessConfig && configText ? (
                  <ConfigBlock
                    title={`${harnessLabel} — remote HTTPS MCP${isOAuth ? ' (OAuth, no key)' : ''}`}
                    description={
                      isOAuth
                        ? `Add the server by URL — sign in in the browser when prompted. Copy into ${harnessConfig.configPath}, then ${harnessConfig.restart}`
                        : `Copy into ${harnessConfig.configPath}, then ${harnessConfig.restart}`
                    }
                    text={configText}
                    copyLabel="harness-config"
                    copied={copied}
                    onCopy={copyToClipboard}
                    copyDisabled={copyBlocked}
                    copyHint={
                      copyBlocked
                        ? isOAuth
                          ? 'Waiting for the server URL — ask an admin to set the public base URL in System Settings.'
                          : isAdmin
                            ? 'Loading the admin key — wait a moment or rotate in step 1.'
                            : 'Generate or rotate your key in step 1 first — the config copies with the real key embedded.'
                        : undefined
                    }
                  />
                ) : (
                  <div className="space-y-2">
                    <h3 className="text-base font-semibold text-harbor-text">
                      Remote MCP configuration
                    </h3>
                    <p>
                      Remote MCP is unavailable until the server&apos;s public URL is configured.
                      Set it in System Settings and reload this page.
                      {remoteMcpUrl && (
                        <>
                          {' '}
                          Expected endpoint: <code>{remoteMcpUrl}</code>
                        </>
                      )}
                    </p>
                  </div>
                )}
              </div>
            ),
          },
          {
            title: 'Verify',
            children: (
              <div className="space-y-4">
                <p>
                  Paste the prompt below into your agent (it must have the{' '}
                  <code className="text-emerald-400">context-harbor</code> MCP server configured
                  from step 2). If the agent returns the{' '}
                  <code className="text-emerald-400">setup_agent_harness</code> install payload,
                  remote MCP is wired up correctly.
                </p>
                <div className="rounded-lg bg-harbor-surface-raised p-4">
                  <pre className="whitespace-pre-wrap font-mono text-sm text-harbor-text-secondary">
                    {verifyPrompt}
                  </pre>
                </div>
                <button
                  type="button"
                  onClick={() => void copyToClipboard(verifyPrompt, 'verify-prompt')}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-harbor-text transition-colors hover:bg-emerald-700"
                >
                  {copied === 'verify-prompt' ? 'Copied!' : 'Copy Verify Prompt'}
                </button>
              </div>
            ),
          },
        ]}
      />

      <OAuthGrantsSection
        grants={oauthGrants}
        isLoading={isLoadingGrants}
        isRevokingId={isRevokingGrantId}
        onRevoke={(id) => void handleRevokeGrant(id)}
      />

      <TokenRevealModal
        isOpen={revealedToken !== null}
        onClose={() => setRevealedToken(null)}
        title="Your MCP API key"
        token={revealedToken}
      />
    </SetupGuideShell>
  )
}
